import { One, OrderDirection } from '@granlupo/api-client/generated/content/zeus'
import type { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState, type ChangeEventHandler } from 'react'
import { useLoadMore } from '../hooks/useLoadMore'
import { serverPropsHandler } from '../libs/next/api/handlers'
import { createFetcher } from '../libs/next/api/jsonApiHandler'
import { websiteContent } from '../libs/next/loaders/graphql/src/contentClient'
import { CheckboxGroupComponent } from '../src/components/form/CheckboxGroupComponent'
import { AdContent } from '../src/components/shared/AdContent'
import { BrandCard } from '../src/components/shared/BrandCard'
import { BrandCardGroup } from '../src/components/shared/BrandCardGroup'
import { Button } from '../src/components/shared/Button'
import { Container } from '../src/components/shared/Container'
import { Hidden } from '../src/components/shared/Hidden'
import { Intro } from '../src/components/shared/Intro'
import { Layout } from '../src/components/shared/Layout'
import { ListingBrands } from '../src/components/shared/ListingBrands'
import { ProductEmptyState } from '../src/components/shared/ProductEmptyState'
import { ShowMoreLessButton } from '../src/components/shared/ShowMoreLessButton'
import { SuggestionModal } from '../src/components/shared/SuggestionModal'
import { TitleEnumeration } from '../src/components/shared/TitleEnumeration'
import { toArray } from '../src/const/ArrayHelper'
import { FilterQuery } from '../src/const/FilterQuery'
import { InternalLink } from '../src/const/Links'
import { VISIBLE_FAVOURITE_BRANDS_AMOUNT } from '../src/const/digitContants'
import { Translation, useTranslate } from '../src/contexts/TranslationContext'
import { AdFragment } from '../src/data/AdFragment'
import { BrandVisualFragment } from '../src/data/BrandFragment'
import { ContentFragment } from '../src/data/ContentFragment'
import { DidYouKnowFragment } from '../src/data/DidYouKnowFragment'
import { ItemSubCategoryPreviewFragment } from '../src/data/ItemSubCategoryFragment'
import { LayoutFragment } from '../src/data/LayoutFragment'
import { SeoFragment } from '../src/data/SeoFragment'
import { loadBrands } from '../src/loaders/loadBrands'
import style from '../src/styles/BrandsPage.module.sass'
import { roundTheCurrentTimeToMinutes } from '../src/utils/roundTheCurrentTimeToMinutes'
import { useDidYouKnowCheck } from '../src/utils/useDidYouKnowCheck'
import type { API } from './api/loaders/[handler]'

type GenericPageProps = InferGetServerSidePropsType<typeof getServerSideProps>

const api = createFetcher<API>('/api/loaders/[handler]')

export default function GenericPage(props: GenericPageProps) {
	const [search, setSearch] = useState({ flavor: [] as string[] })
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isButtonMoreVisible, setIsButtonMoreVisible] = useState(true)
	const [searchValue, setSearchValue] = useState('')
	const router = useRouter()

	const exceededCardLimit = props.data.listBrand.length > VISIBLE_FAVOURITE_BRANDS_AMOUNT

	const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
		setSearchValue(event.currentTarget.value)
	}
	const toggleButtonMoreVisible = useCallback(() => setIsButtonMoreVisible(!isButtonMoreVisible), [isButtonMoreVisible])
	const toggleModalOpen = useCallback((value: boolean) => setIsModalOpen(value), [])

	const handleClose = useCallback(() => {
		setSearchValue('')
	}, [setSearchValue])

	useEffect(() => {
		setSearch({
			flavor: toArray(router.query.flavor),
		})
	}, [router])

	const query = useLoadMore(['brands', JSON.stringify(search), searchValue], props.listingBrands, async (page) =>
		api('brands', { _req: { query: { page } }, search, searchValue }),
	)

	const page = props.data.getBrandsPage

	const listedFavouriteBrands = isButtonMoreVisible
		? props.data.listBrand.slice(0, VISIBLE_FAVOURITE_BRANDS_AMOUNT)
		: props.data.listBrand

	const allBrands = query.data?.pages.flatMap((page) => page.items) ?? []

	const translate = useTranslate()

	const titleArr = page?.title?.includes(' a ') ? page.title.split(' a ') : page?.title
	const { preview } = router.query

	return (
		<Layout
			didYouKnow={useDidYouKnowCheck(props.data.listDidYouKnow)}
			hasBreadcrumbs
			footer={props.data.footer.getFooter}
			socials={props.data.socials.listSocial}
			seo={props.data.getBrandsPage?.seo}
			url={InternalLink.Brands}
		>
			<SuggestionModal isModalOpen={isModalOpen} handleClose={() => toggleModalOpen(false)} />
			<Container size="wide" disableGutters>
				<div className={style.content}>
					<AdContent ads={props.data.listAd ?? []} isAlignedRight />
					<Container size="narrow">
						<div className={style.introWrapper}>
							<Intro
								title={
									page?.title ? (
										typeof titleArr === 'object' ? (
											<>
												{titleArr[0]}
												<br /> a {titleArr[1]}
											</>
										) : (
											page.title
										)
									) : (
										<>
											<Translation tr="brandsPage.title.first" />
											<br />
											<Translation tr="brandsPage.title.second" />
										</>
									)
								}
								leftAligned
								lead={preview ? (page?.draft?.blocks ?? []) : (page?.content?.blocks ?? [])}
								withAd
							/>
						</div>
						<div className={style.container}>
							<Container size="narrow" disableGutters>
								{props.data.listBrand && props.data.listBrand.length > 0 && (
									<>
										<Hidden breakpoint="mobileDown">
											<div className={style.favoriteBrandsWrapper}>
												<BrandCardGroup brands={props.data.listBrand} title={'favouriteBrands.title'} />
											</div>
										</Hidden>
										<Hidden breakpoint="mobileUp">
											<div className={style.mobileGridTitle} id="anchorFavouriteBrands">
												<Translation tr="favouriteBrands.title" />
											</div>
											<div className={style.mobileGrid}>
												{listedFavouriteBrands.map((brand) => (
													<BrandCard key={brand.name} brand={brand} />
												))}
											</div>
											<div className={style.buttonWrapper}>
												{exceededCardLimit && (
													<ShowMoreLessButton
														anchorId="anchorFavouriteBrands"
														moreIsVisible={isButtonMoreVisible}
														onClick={toggleButtonMoreVisible}
													/>
												)}
											</div>
										</Hidden>
									</>
								)}
							</Container>
						</div>

						<div className={style.filterWrapper}>
							<CheckboxGroupComponent
								title={translate('brands.section.checkboxGroup.title')}
								items={props.data.listItemSubCategory}
								sum={query.data?.pages[0].totalCount ?? 0}
								isFilter
								initialQuery={props.initialQuery}
								label="flavor"
							/>
						</div>
						<TitleEnumeration
							title={translate('brands.section.titleEnumeration.title')}
							sum={query.data?.pages[0].totalCount ?? 0}
							buttonLabel={translate('brands.section.titleEnumeration.button')}
							onChange={handleChange}
							searchedText={searchValue}
							handleClose={handleClose}
						/>
						{allBrands && allBrands.length > 0 ? (
							<div id="title">
								<ListingBrands
									brands={allBrands}
									onNextPage={query.fetchNextPage}
									hasNextPage={Boolean(query.hasNextPage)}
									totalCount={query.data?.pages[0]?.totalCount ?? 0}
								/>
							</div>
						) : (
							<ProductEmptyState
								title={translate('brands.section.emptyState.title')}
								subtitle={
									<span className={style.emptyStateButton}>
										<Translation tr="brandsPage.emptyState.subtitle.first" />{' '}
										<Button
											type={{ variant: 'underlined' }}
											label={translate('brandsPage.emptyState.button')}
											onClick={() => toggleModalOpen(true)}
										/>{' '}
										<Translation tr="emptyState.subtitle.second" />
									</span>
								}
							/>
						)}
					</Container>
				</div>
			</Container>
		</Layout>
	)
}

// export const runtime = 'experimental-edge'
export const getServerSideProps = serverPropsHandler(async (context: GetServerSidePropsContext) => {
	context.res.setHeader('Cache-Control', 's-maxage=60, stale-while-revalidate=3600')

	const content = websiteContent

	const data = await content('query')({
		...LayoutFragment(),
		listItemSubCategory: [
			{ orderBy: [{ remoteId: OrderDirection.asc }], filter: { isFlavorCategory: { eq: true } } },
			ItemSubCategoryPreviewFragment(),
		],
		listAd: [
			{
				filter: {
					isBrandSectionVisible: { eq: true },
					isActive: { eq: true },
					activeFrom: { lte: roundTheCurrentTimeToMinutes() },
					activeUntil: { gte: roundTheCurrentTimeToMinutes() },
				},
			},
			AdFragment(),
		],
		listBrand: [
			{
				filter: { promotedOnBrandsPagePlace: { notEq: 0 } },
				orderBy: [{ promotedOnBrandsPagePlace: OrderDirection.asc }],
			},
			BrandVisualFragment(),
		],
		getBrandsPage: [
			{ by: { unique: One.One } },
			{
				title: true,
				draft: [{}, ContentFragment()],
				content: [{}, ContentFragment()],
				seo: [{}, SeoFragment()],
			},
		],
		listDidYouKnow: [
			{
				limit: 40,
				orderBy: [
					{
						_random: true,
					},
				],
			},

			DidYouKnowFragment(),
		],
	})

	return {
		props: {
			data,
			listingBrands: await loadBrands(1),
			initialQuery: FilterQuery.parse(context.query),
		},
	}
})
